import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  mvpApiGetJobTask,
  client,
  type JobTaskDetailSchema,
  type OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import {
  useFetchJobTasks,
  imageGalleryQueryOptions,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { selectNextPrefetchTaskId } from "./useNextTaskPrefetch";
import {
  getNextPendingTaskData,
  resolveGalleryImageUrl,
} from "../utils/helpers";

const STALE_TIME = 30 * 1000;

type Args = {
  jobId: string | undefined;
  currentTaskId: string | undefined;
  enabled: boolean;
};

export function useCoveragePrefetch({ jobId, currentTaskId, enabled }: Args) {
  const { data: jobTasks } = useFetchJobTasks(
    enabled && jobId ? jobId : undefined,
  );

  const nextTaskId: string | undefined = useMemo(() => {
    if (!enabled) return undefined;
    return selectNextPrefetchTaskId(jobTasks?.tasks, currentTaskId);
  }, [enabled, jobTasks, currentTaskId]);

  const { data: nextTask } = useQuery<JobTaskDetailSchema>({
    queryKey: ["fetch-task", jobId, nextTaskId],
    queryFn: async () => {
      const r = await mvpApiGetJobTask({
        path: { job_id: jobId as string, task_id: nextTaskId as string },
      });
      if (r.error) throw new Error(String(r.error));
      return r.data as JobTaskDetailSchema;
    },
    enabled: !!enabled && !!jobId && !!nextTaskId,
    staleTime: STALE_TIME,
  });

  const nextParcelId = (nextTask?.data as any)?.parcel_id as string | undefined;

  const { data: nextParcel } = useQuery<OoSParcelSchema>({
    queryKey: ["parcel", nextParcelId],
    queryFn: async () => {
      const r = await client.get({ url: `/v1/oos-parcels/${nextParcelId}` });
      if (r.error) throw new Error(String(r.error));
      return r.data as OoSParcelSchema;
    },
    enabled: !!enabled && !!nextParcelId,
    staleTime: STALE_TIME,
  });

  const enriched = useMemo(() => {
    if (!nextParcel || !nextTask) return null;
    return getNextPendingTaskData(nextParcel, nextTask.data, nextTask);
  }, [nextParcel, nextTask]);

  const imageId = enriched?.image_id as string | undefined;
  const galleryIds = imageId ? [imageId] : undefined;
  const galleryOptions = imageGalleryQueryOptions(galleryIds);

  const { data: gallery } = useQuery({
    ...galleryOptions,
    enabled: !!enabled && (galleryOptions.enabled ?? false),
  });

  const rawImageUrl = resolveGalleryImageUrl(gallery?.images?.[0]);

  // Best-effort: warm the browser HTTP cache with the image bytes.
  // Not required for the cache flip — just eliminates the BoxDrawImage
  // loading spinner when the labeler gives the download enough time.
  useEffect(() => {
    if (!enabled || !rawImageUrl) return;
    const img = new Image();
    img.src = rawImageUrl;
    return () => {
      img.src = "";
    };
  }, [enabled, rawImageUrl]);

  // Data ready: enough to flip the cache and skip all API round trips on submit.
  // imageLoaded is best-effort — if it's true the image renders instantly too,
  // but we don't require it for the flip since the full image download can
  // outlast a fast labeler's time on a task.
  const isPrefetchReady = !!(
    enabled &&
    nextTaskId &&
    nextTask &&
    nextParcel &&
    gallery
  );

  return { nextTaskId, isPrefetchReady };
}
